import {FeatureCollection, LineString, Point, Polygon} from "geojson"
import {apiCall} from "@ui/api/common"

export type TNavObject = {
  id: number
}
type TWithObjName = {
  obj_name?: string
  national_obj_name?: string
}
type TWithInformation = {
  information?: string
  national_information?: string
}
export type TFullNavObject = TNavObject & TWithObjName & TWithInformation
type TLightProps = {
  light_type?: string
  signal_period?: string
  signal_group?: string
  colour?: string[]
} & TFullNavObject
export type TWitlLights = {
  lights?: TLightProps[]
}

export type TBuoyProps = {
  icon_name: string
  buoy_shape?: string
  mark_type: string
} & TFullNavObject & TWitlLights

export type TBuoyLateralProps = {lateral_mark_category: string} & TBuoyProps
export type TBuoySafeWaterProps = TBuoyProps
export type TBuoySpecialPurposeProps = TBuoyProps

export type TGetBuoysResponse = {
  lateral: FeatureCollection<Point, TBuoyLateralProps>
  safe_water: FeatureCollection<Point, TBuoySafeWaterProps>
  special_purpose: FeatureCollection<Point, TBuoySpecialPurposeProps>
}

export type TBeaconProps = {
  icon_name: string
  mark_type: string
  is_noticeable?: boolean
} & TFullNavObject & TWitlLights

export type TBeaconLateralProps = {lateral_mark_category: string} & TBeaconProps
export type TBeaconSpecialPurpose = TBeaconProps
export type TAggregation = {
  beacons?: TNavObject[]
  lines?: TNavObject[]
  axes?: TNavObject[]
  lights?: TNavObject[]
} & TFullNavObject

export type TGetBeaconsResponse = {
  lateral: FeatureCollection<Point, TBeaconLateralProps>
  special_purpose: FeatureCollection<Point, TBeaconSpecialPurpose>
  aggregations: TAggregation[]
}

export type TNavigationLineProps = TNavObject & TWithInformation
export type TRecommendedTrackProps = TFullNavObject

export type TGetLinesResponse = {
  navigation_line: FeatureCollection<LineString, TNavigationLineProps>
  recommended_track: FeatureCollection<LineString, TRecommendedTrackProps>
}

export type TAnchorageAreaProps = {
  anchorage_category: string[]
  association_id?: number[]
} & TFullNavObject
export type TAssociation = {
  aggrs?: TNavObject[]
  achares?: TNavObject[]
  boylats?: TNavObject[]
} & TFullNavObject

export type TGetPolygonsResponse = {
  anchorage_area: FeatureCollection<Polygon, TAnchorageAreaProps>
  associations: TAssociation[]
}

export type TObstructionProps = {
  icon_name: string
  obstruction_category?: string
  sounding_value?: number
  water_level: string
  sounding_exposition?: string
  surface_nature?: string[]
} & TFullNavObject
export type TAnchorBerthProps = {
  icon_name: string
  anchorage_category: string[]
} & TFullNavObject
export type TWreckProps = {
  icon_name: string
  water_level: string
  is_noticeable?: boolean
  wreck_category?: string
} & TFullNavObject
export type TDistanceMarkProps = {
  distance_mark_category?: string
} & TFullNavObject

export type TGetOthersResponse = {
  obstruction: FeatureCollection<Point | LineString | Polygon, TObstructionProps>,
  anchor_berth: FeatureCollection<Point, TAnchorBerthProps>,
  wreck: FeatureCollection<Point, TWreckProps>,
  distance_mark: FeatureCollection<Point, TDistanceMarkProps>
}

export type TPoiProps = {
  type: string
  color: number
  title: string
}

type TLayerData = {
  seconds: number,
  min_zoom: number | null,
  max_zoom: number | null
}
type TLayerResponse = {[name: string]: TLayerData}

export const getLayers = () => apiCall().get<TLayerResponse>("geodata/layers")

export type TXY = {x: number, y: number}
export type TGetFeaturesByXYProps = {
  path: string,
  baseUrl?: string
} & TXY

const getFeaturesByXY = async <T>({path, x, y}: TGetFeaturesByXYProps) =>
  apiCall().get<T>(`/geodata/${path}/${x}/${y}`)

export const getCities = ({x, y}: TXY) =>
  getFeaturesByXY<FeatureCollection<Point>>({path: "cities", x, y})

export const getTowns = ({x, y}: TXY) =>
  getFeaturesByXY<FeatureCollection<Point>>({path: "towns", x, y})

export const getDepths = ({x, y}: TXY) =>
  getFeaturesByXY<FeatureCollection<Point>>({path: "depths", x, y})

export const getBuoys = ({x, y}: TXY) =>
  getFeaturesByXY<TGetBuoysResponse>({path: "buoys", x, y})

export const getBeacons = ({x, y}: TXY) =>
  getFeaturesByXY<TGetBeaconsResponse>({path: "beacons", x, y})

export const getLines = ({x, y}: TXY) =>
  getFeaturesByXY<TGetLinesResponse>({path: "lines", x, y})

export const getPolygons = ({x, y}: TXY) =>
  getFeaturesByXY<TGetPolygonsResponse>({path: "polygons", x, y})

export const getOthers = ({x, y}: TXY) =>
  getFeaturesByXY<TGetOthersResponse>({path: "others", x, y})

export const getPoi = ({x, y}: TXY) =>
  getFeaturesByXY<FeatureCollection<Point, TPoiProps>>({path: "poi", x, y})
